import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { CheckCircle, XCircle, Loader, ArrowRight } from 'lucide-react';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('Verifying your email...');

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                setStatus('error');
                setMessage('Invalid verification link. Token is missing.');
                return;
            }

            try {
                await api.post('/auth/verify-email', { token });
                setStatus('success');
                setMessage('Your email has been verified successfully!');
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 5000);
            } catch (error) {
                setStatus('error');
                setMessage(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    'Failed to verify email. The link may have expired or is invalid.'
                );
            }
        };

        verifyEmail();
    }, [token, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                {status === 'verifying' && (
                    <div className="flex flex-col items-center animate-fadeIn">
                        <Loader className="h-16 w-16 text-blue-500 animate-spin mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Email</h2>
                        <p className="text-gray-600">{message}</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="flex flex-col items-center animate-fadeIn">
                        <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                            <CheckCircle className="h-10 w-10 text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Verified!</h2>
                        <p className="text-gray-600 mb-8">{message}</p>
                        <p className="text-sm text-gray-400 mb-6">Redirecting to login in 5 seconds...</p>
                        <Link
                            to="/login"
                            className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center gap-2"
                        >
                            Continue to Login <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center animate-fadeIn">
                        <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
                            <XCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
                        <p className="text-gray-600 mb-8">{message}</p>
                        <Link
                            to="/login"
                            className="text-blue-600 font-medium hover:text-blue-800 transition-colors"
                        >
                            Back to Login
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail;
